/**
 * TenantTrafficTab — Aylık kullanım + 30 günlük timeline.
 */
import { useMemo } from "react";
import { Card, CardHeader, Eyebrow } from "@/components/design-system";
import { Progress } from "@/components/ui/progress";
import { Activity, Calendar } from "lucide-react";
import { fmtDate } from "../_tenant-helpers";

export default function TenantTrafficTab({ data }) {
    const t = useMemo(() => data.traffic || {}, [data.traffic]);
    const timeline = useMemo(() => t.timeline_30d || [], [t.timeline_30d]);
    const maxCount = useMemo(
        () => timeline.reduce((m, d) => Math.max(m, d.count || 0), 0) || 1,
        [timeline]
    );
    const sum30 = useMemo(
        () => timeline.reduce((s, d) => s + (d.count || 0), 0),
        [timeline]
    );

    const pct = Math.min(100, t.usage_pct || 0);
    const tone = pct >= 100 ? "[&>div]:bg-rose-500" : pct >= 80 ? "[&>div]:bg-amber-500" : "[&>div]:bg-emerald-500";

    return (
        <div className="space-y-5" data-testid="traffic-tab">
            {/* Monthly summary */}
            <Card>
                <CardHeader
                    title="Bu Ay"
                    desc={`${fmtDate(t.month_window?.start)} – ${fmtDate(t.month_window?.end)} aralığında faturalanabilir etkileşim.`}
                />
                <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-5">
                    <KPI label="Bu Ay Kullanım" value={fmt(t.usage_this_month)} testId="traffic-month-usage" />
                    <KPI label="Aylık Kota" value={fmt(t.quota_this_month)} testId="traffic-month-quota" />
                    <KPI label="Doluluk" value={`%${pct.toFixed(1)}`} tone={pct >= 100 ? "danger" : pct >= 80 ? "warn" : null} testId="traffic-month-pct" />
                </div>
                <div className="px-5 pb-5">
                    <Progress value={pct} className={`h-2 ${tone}`} data-testid="traffic-progress" />
                </div>
            </Card>

            {/* 30-day timeline */}
            <Card>
                <CardHeader
                    title="Son 30 Gün"
                    desc="Günlük faturalanabilir modal/drawer açılışları. Çubuğun üstündeki rakam o günün toplamı."
                    right={<Calendar className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />}
                />
                <div className="p-5">
                    {timeline.length === 0 ? (
                        <div className="text-center py-10 text-xs text-muted-foreground" data-testid="traffic-empty">
                            Son 30 günde kayıtlı etkileşim yok.
                        </div>
                    ) : (
                        <>
                            <div className="flex items-end gap-1 h-32 overflow-x-auto scroll-thin" data-testid="traffic-timeline">
                                {timeline.map((d) => {
                                    const h = Math.round((d.count / maxCount) * 100);
                                    return (
                                        <div key={d.date} className="flex flex-col items-center gap-1 min-w-[12px] flex-1">
                                            <div className="text-[9px] text-muted-foreground">{d.count}</div>
                                            <div
                                                className="w-full bg-foreground/80 rounded-sm hover:bg-foreground transition-colors"
                                                style={{ height: `${Math.max(2, h)}%` }}
                                                title={`${d.date}: ${d.count}`}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="mt-3 flex items-center justify-between text-[10px] text-muted-foreground">
                                <span>{timeline[0]?.date}</span>
                                <span>Toplam 30 gün: <b className="text-foreground">{fmt(sum30)}</b></span>
                                <span>{timeline[timeline.length - 1]?.date}</span>
                            </div>
                        </>
                    )}
                </div>
            </Card>
        </div>
    );
}

function fmt(n) {
    if (n == null) return "—";
    return new Intl.NumberFormat("tr-TR").format(n);
}

function KPI({ label, value, tone, testId }) {
    const valueCls =
        tone === "danger" ? "text-rose-600" :
        tone === "warn"   ? "text-amber-600" :
                            "text-foreground";
    return (
        <div data-testid={testId}>
            <Eyebrow>{label}</Eyebrow>
            <div className={`font-display text-2xl font-bold tracking-tight mt-1 ${valueCls}`}>
                {value}
            </div>
        </div>
    );
}
